import { Quote, Star, ExternalLink } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { CtaBand } from "@/components/CtaBand";
import { StarRating } from "@/components/StarRating";
import { useReviews } from "@/hooks/useReviews";
import { container, sectionPad, cardShadow } from "@/lib/ui";
import { localBusinessSchema, breadcrumbSchema } from "@/lib/schema";

export default function ReviewsPage() {
  const { reviews, rating, url, source } = useReviews();

  return (
    <>
      <Seo
        title="Reviews — What Melbourne Customers Say"
        description="Read genuine 5-star reviews for JK House Cleaning from Melbourne customers. Reliable, professional, detail-focused residential cleaning."
        path="/reviews"
        schema={[localBusinessSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Reviews", path: "/reviews" }])]}
      />
      <PageHeader
        eyebrow="Reviews"
        title="Loved by Melbourne homes"
        subtitle="Genuine feedback from real customers."
        crumbs={[{ name: "Reviews" }]}
      />

      <section className={`${sectionPad} bg-white`}>
        <div className={container}>
          <div className="flex flex-col items-center gap-3 mb-12">
            <div className="flex items-center gap-3">
              <StarRating value={Math.round(rating)} size="w-6 h-6" />
              <span className="font-heading text-lg font-bold text-navy">{Number(rating).toFixed(1)} average · Google Reviews</span>
            </div>
            {url && (
              <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brandgreen-dark hover:text-brandgreen">
                View on Google <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {reviews.map((r, i) => (
              <figure key={i} className={`flex flex-col rounded-2xl bg-neutralbg/60 p-7 border border-gray-100 ${cardShadow}`} data-testid={`reviews-page-${i}`}>
                <Quote className="w-8 h-8 text-brandgreen/30" />
                <StarRating value={r.rating} size="w-4 h-4" className="mt-3" />
                <blockquote className="mt-4 text-sm text-gray-700 leading-relaxed flex-1">"{r.text}"</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  {r.photo ? (
                    <img src={r.photo} alt="" className="w-10 h-10 rounded-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="grid place-items-center w-10 h-10 rounded-full bg-navy text-white font-heading font-semibold">{r.name.charAt(0)}</span>
                  )}
                  <span>
                    <span className="block font-semibold text-navy text-sm">{r.name}</span>
                    <span className="block text-xs text-gray-400">{r.date} · Google</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>

          {source !== "google" && (
            <div className="mt-12 rounded-2xl border border-dashed border-gray-200 bg-neutralbg/60 p-8 text-center">
              <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-white shadow-sm mx-auto">
                <Star className="w-7 h-7 text-yellow-400 fill-yellow-400" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-navy">More reviews coming soon</h2>
              <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                Our Google Business Profile is being connected. Once linked, verified Google reviews will appear here automatically.
              </p>
            </div>
          )}
        </div>
      </section>

      <CtaBand />
    </>
  );
}
